import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  FileText,
  X,
  Check,
  Plus,
  Trash2,
  RotateCcw,
  AlertCircle,
  History,
  Wrench,
  Battery,
  StickyNote
} from 'lucide-react';
import { serviceCallsAPI, serviceCallVisitsAPI, clientsAPI, serviceCallTypesAPI, cspireAPI, franklinAPI } from '@shared/apiClient';
import { formatDate, formatCurrency, getClientDisplayName } from '@shared/utils';
import toast from 'react-hot-toast';
import InventorySearch from './InventorySearch';

export default function ServiceCallModal({ serviceCall, isOpen, onClose, isRepairCall = false }) {
  const [activeTab, setActiveTab] = useState('service'); // 'service' | 'client' | 'techNotes'
  const [techNotes, setTechNotes] = useState('');
  const [initialTechNotes, setInitialTechNotes] = useState('');
  const [isSavingTechNotes, setIsSavingTechNotes] = useState(false);
  const [formData, setFormData] = useState({
    workPerformed: '',
    partsUsed: [],
    generatorModel: '',
    generatorSerialNumber: '',
    generatorInfoUnreadable: false,
    returnReason: '',
    typeId: '',
    laborHours: '',
    generatorHours: '',
    mileage: '',
    fuelLevel: ''
  });
  const [clientFormData, setClientFormData] = useState({
    generatorType: '',
    generatorModel: '',
    generatorSerialNumber: '',
    generatorSize: '',
    generatorCoolingType: '',
    generatorExerciseTime: '',
    airFilter: '',
    fuelFilter1: '',
    fuelFilter2: '',
    oilFilter1: '',
    oilFilter2: '',
    fuelAdditive: '',
    fuelType: '',
    oilType: '',
    oilAmount: '',
    batterySize: '',
    batteryDateCode: '',
    batteryDateChanged: ''
  });
  const [initialClientData, setInitialClientData] = useState(null);
  const [newPart, setNewPart] = useState({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);
  const [useCustomPart, setUseCustomPart] = useState(false);
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [actionType, setActionType] = useState(null); // 'complete', 'return', or 'visit'
  const [errors, setErrors] = useState({});
  const queryClient = useQueryClient();

  // Fetch service call types
  const { data: typesData } = useQuery(
    'serviceCallTypes',
    () => serviceCallTypesAPI.getServiceCallTypes({ includeInactive: false }),
    { enabled: isOpen }
  );

  const serviceCallTypes = typesData?.data?.types || [];

  useEffect(() => {
    if (serviceCall && serviceCallTypes.length > 0) {
      const client = serviceCall.client;
      const isOilChangeMonth = client?.isOilChangeMonth || false;
      
      // Determine default type based on call type
      let defaultTypeId = '';
      if (isRepairCall) {
        // Default to "repair" for repair calls
        const repairType = serviceCallTypes.find(t => t.name.toLowerCase() === 'repair');
        defaultTypeId = repairType?.id || '';
      } else if (isOilChangeMonth) {
        // Default to "service" for oil change months
        const serviceType = serviceCallTypes.find(t => t.name.toLowerCase() === 'service');
        defaultTypeId = serviceType?.id || '';
      } else {
        // Default to "inspection" for non-oil change months
        const inspectionType = serviceCallTypes.find(t => t.name.toLowerCase() === 'inspection');
        defaultTypeId = inspectionType?.id || '';
      }

      // Check if there's saved draft data in localStorage
      const draftKey = `serviceCallDraft_${serviceCall.id}`;
      const savedDraft = localStorage.getItem(draftKey);
      
      const latestVisit = serviceCall.visits?.length
        ? serviceCall.visits[serviceCall.visits.length - 1]
        : null;
      const baseFuelLevel = latestVisit?.fuelLevel || '';

      if (savedDraft) {
        try {
          const parsedDraft = JSON.parse(savedDraft);
          // Don't let a stale draft blank out generator info that is already on file
          // for the client.
          if (!parsedDraft.generatorModel) parsedDraft.generatorModel = client?.generatorModel || '';
          if (!parsedDraft.generatorSerialNumber) parsedDraft.generatorSerialNumber = client?.generatorSerialNumber || '';
          setFormData({ fuelLevel: '', ...parsedDraft });
          toast.success('Draft restored!', { duration: 2000 });
        } catch (error) {
          console.error('Error parsing saved draft:', error);
          // Fall back to default data
          setFormData({
            workPerformed: serviceCall.workPerformed || '',
            partsUsed: serviceCall.partsUsed || [],
            generatorModel: client?.generatorModel || '',
            generatorSerialNumber: client?.generatorSerialNumber || '',
            generatorInfoUnreadable: false,
            returnReason: serviceCall.internalNotes || '',
            typeId: serviceCall.typeId || defaultTypeId,
            laborHours: serviceCall.laborHours || '',
            generatorHours: serviceCall.generatorHours || '',
            mileage: serviceCall.mileage || '',
            fuelLevel: baseFuelLevel
          });
        }
      } else {
        setFormData({
          workPerformed: serviceCall.workPerformed || '',
          partsUsed: serviceCall.partsUsed || [],
          generatorModel: client?.generatorModel || '',
          generatorSerialNumber: client?.generatorSerialNumber || '',
          generatorInfoUnreadable: false,
          returnReason: serviceCall.internalNotes || '',
          typeId: serviceCall.typeId || defaultTypeId,
          laborHours: serviceCall.laborHours || '',
          generatorHours: serviceCall.generatorHours || '',
          mileage: serviceCall.mileage || '',
          fuelLevel: baseFuelLevel
        });
      }
      
      // Initialize client data
      if (client) {
        const clientData = {
          generatorType: client.generatorType || '',
          generatorModel: client.generatorModel || '',
          generatorSerialNumber: client.generatorSerialNumber || '',
          generatorSize: client.generatorSize || '',
          generatorCoolingType: client.generatorCoolingType || '',
          generatorExerciseTime: client.generatorExerciseTime || '',
          airFilter: client.airFilter || '',
          fuelFilter1: client.fuelFilter1 || '',
          fuelFilter2: client.fuelFilter2 || '',
          oilFilter1: client.oilFilter1 || '',
          oilFilter2: client.oilFilter2 || '',
          fuelAdditive: client.fuelAdditive || '',
          fuelType: client.fuelType || '',
          oilType: client.oilType || '',
          oilAmount: client.oilAmount || '',
          batterySize: client.batterySize || '',
          batteryDateCode: client.batteryDateCode || '',
          batteryDateChanged: client.batteryDateChanged ? new Date(client.batteryDateChanged).toISOString().split('T')[0] : ''
        };
        setClientFormData(clientData);
        setInitialClientData(clientData);
      }

      const cspireSite = serviceCall.cspireWorkOrder?.site;
      const franklinSite = serviceCall.franklinSite;
      const seededTechNotes = serviceCall.isFranklin
        ? (franklinSite?.notes || '')
        : serviceCall.isCspire
          ? (cspireSite?.notes || '')
          : (client?.notes || '');
      setTechNotes(seededTechNotes);
      setInitialTechNotes(seededTechNotes);

      setActionType(null);
      setErrors({});
    }
  }, [serviceCall, serviceCallTypes, isRepairCall]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Auto-save form data to localStorage whenever it changes
  useEffect(() => {
    if (serviceCall && isOpen && formData.workPerformed !== undefined) {
      const draftKey = `serviceCallDraft_${serviceCall.id}`;
      localStorage.setItem(draftKey, JSON.stringify(formData));
    }
  }, [formData, serviceCall, isOpen]);

  const updateServiceCallMutation = useMutation(
    (data) => serviceCallsAPI.updateServiceCall(serviceCall.id, data),
    {
      onSuccess: (response, variables) => {
        const isCompleted = variables.status === 'completed';
        // Check if it's a visit by checking the type name in the response or variables
        const visitType = serviceCallTypes.find(t => t.name.toLowerCase() === 'visit');
        const isVisit = visitType && (variables.typeId === visitType.id || response?.data?.serviceCall?.type?.name?.toLowerCase() === 'visit');
        let message = '';
        if (isVisit) {
          message = 'Service call marked as visit successfully!';
        } else if (isCompleted) {
          message = 'Service call completed successfully!';
        } else {
          message = 'Service call marked as return';
        }
        
        // Clear the saved draft from localStorage
        const draftKey = `serviceCallDraft_${serviceCall.id}`;
        localStorage.removeItem(draftKey);
        
        toast.success(message);
        queryClient.invalidateQueries('weeklyAssignments');
        queryClient.invalidateQueries('priorityAssignments');
        queryClient.invalidateQueries('myServiceCalls');
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update service call');
      }
    }
  );

  const updateClientMutation = useMutation(
    (data) => clientsAPI.updateClientGeneratorInfo(serviceCall.client.id, data),
    {
      onSuccess: () => {
        toast.success('Customer information updated successfully!');
        queryClient.invalidateQueries('weeklyAssignments');
        queryClient.invalidateQueries('priorityAssignments');
        queryClient.invalidateQueries('clients');
        queryClient.invalidateQueries('allClients');
      },
      onError: (error) => {
        console.error('Failed to update customer generator info:', error);
        toast.error(error.response?.data?.message || 'Failed to update customer');
        // Don't show error toast for client update failures, just log it
      }
    }
  );

  const handleInventorySelect = (inventoryItem) => {
    setSelectedInventoryItem(inventoryItem);
    setNewPart({
      inventoryId: inventoryItem.inventoryId || '',
      itemNumber: inventoryItem.itemNumber,
      description: inventoryItem.description,
      name: inventoryItem.itemNumber,
      quantity: inventoryItem.quantity || 1,
      cost: inventoryItem.price || 0
    });
    setUseCustomPart(false); // Switch back to inventory mode when item is selected
  };

  const handleAddPart = () => {
    if (useCustomPart) {
      // Custom part mode - require name, quantity, and cost
      if (newPart.name && newPart.quantity && newPart.cost !== undefined) {
        setFormData({
          ...formData,
          partsUsed: [...formData.partsUsed, {
            itemNumber: newPart.name, // Use name as itemNumber for custom parts
            description: newPart.description || newPart.name,
            name: newPart.name,
            quantity: parseFloat(newPart.quantity),
            cost: parseFloat(newPart.cost)
          }]
        });
        setNewPart({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
        setSelectedInventoryItem(null);
        setSearchResetKey(k => k + 1);
      } else {
        toast.error('Please enter part name, quantity, and cost');
      }
    } else {
      // Inventory mode - require selected item and quantity
      if (newPart.itemNumber && newPart.quantity && newPart.cost !== undefined) {
        setFormData({
          ...formData,
          partsUsed: [...formData.partsUsed, {
            ...(newPart.inventoryId ? { inventoryId: newPart.inventoryId } : {}),
            itemNumber: newPart.itemNumber,
            description: newPart.description || newPart.name,
            name: newPart.itemNumber, // For backward compatibility
            quantity: parseFloat(newPart.quantity),
            cost: parseFloat(newPart.cost)
          }]
        });
        setNewPart({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
        setSelectedInventoryItem(null);
        setSearchResetKey(k => k + 1);
      } else {
        toast.error('Please select a part and enter quantity');
      }
    }
  };

  const handleToggleMode = () => {
    setUseCustomPart(!useCustomPart);
    setSelectedInventoryItem(null);
    setNewPart({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
  };

  const handleRemovePart = (index) => {
    setFormData({
      ...formData,
      partsUsed: formData.partsUsed.filter((_, i) => i !== index)
    });
  };

  const handleClearDraft = () => {
    if (window.confirm('Are you sure you want to clear the saved draft and start fresh?')) {
      const draftKey = `serviceCallDraft_${serviceCall.id}`;
      localStorage.removeItem(draftKey);
      
      // Reset form to initial defaults
      const client = serviceCall.client;
      const isOilChangeMonth = client?.isOilChangeMonth || false;
      
      let defaultTypeId = '';
      if (isRepairCall) {
        const repairType = serviceCallTypes.find(t => t.name.toLowerCase() === 'repair');
        defaultTypeId = repairType?.id || '';
      } else if (isOilChangeMonth) {
        const serviceType = serviceCallTypes.find(t => t.name.toLowerCase() === 'service');
        defaultTypeId = serviceType?.id || '';
      } else {
        const inspectionType = serviceCallTypes.find(t => t.name.toLowerCase() === 'inspection');
        defaultTypeId = inspectionType?.id || '';
      }

      setFormData({
        workPerformed: serviceCall.workPerformed || '',
        partsUsed: serviceCall.partsUsed || [],
        generatorModel: client?.generatorModel || '',
        generatorSerialNumber: client?.generatorSerialNumber || '',
        generatorInfoUnreadable: false,
        returnReason: serviceCall.internalNotes || '',
        typeId: serviceCall.typeId || defaultTypeId,
        laborHours: serviceCall.laborHours || '',
        generatorHours: serviceCall.generatorHours || '',
        mileage: serviceCall.mileage || ''
      });
      setActionType(null);
      setErrors({});
      toast.success('Draft cleared!');
    }
  };

  const validateForm = (action) => {
    const newErrors = {};

    if (!formData.workPerformed.trim()) {
      newErrors.workPerformed = 'Work performed is required';
    }

    // For visits, only description is required (no parts, no generator info)
    if (action === 'visit') {
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    // Generator info validation - required unless marked as unreadable (not for visits).
    // Skip the requirement entirely if the model + serial are already on file for the
    // client, since the techs are not being asked to capture new info in that case.
    const existingClient = serviceCall?.client;
    const generatorInfoOnFile =
      existingClient?.generatorModel && existingClient?.generatorSerialNumber;
    if (!formData.generatorInfoUnreadable && !generatorInfoOnFile) {
      if (!formData.generatorModel.trim()) {
        newErrors.generatorModel = 'Generator model is required';
      }
      if (!formData.generatorSerialNumber.trim()) {
        newErrors.generatorSerialNumber = 'Generator serial number is required';
      }
    }

    // Return reason validation if marking as return
    if (action === 'return' && !formData.returnReason.trim()) {
      newErrors.returnReason = 'Return reason is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClientChange = (e) => {
    const { name, value } = e.target;
    setClientFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    
    // Handle client update
    const updateData = {};
    
    // Compare each field with initial client data
    Object.keys(clientFormData).forEach(key => {
      if (clientFormData[key] !== initialClientData[key]) {
        updateData[key] = clientFormData[key]?.trim() || null;
      }
    });
    
    // Only submit if there are changes
    if (Object.keys(updateData).length === 0) {
      toast.info('No changes to save');
      return;
    }
    
    try {
      await updateClientMutation.mutateAsync(updateData);
    } catch (error) {
      // Error is already handled in the mutation
    }
  };

  const handleSaveTechNotes = async (e) => {
    e?.preventDefault?.();
    const trimmed = (techNotes || '').trim();
    if (trimmed === (initialTechNotes || '').trim()) {
      toast('No changes to save', { icon: 'ℹ️' });
      return;
    }
    setIsSavingTechNotes(true);
    try {
      const payload = trimmed.length > 0 ? trimmed : null;
      if (serviceCall?.isFranklin) {
        const siteId = serviceCall.franklinSite?.id;
        if (!siteId) {
          toast.error('No Franklin site linked to this assignment');
          return;
        }
        await franklinAPI.updateSiteTechNotes(siteId, payload);
      } else if (serviceCall?.isCspire) {
        const siteId = serviceCall.cspireWorkOrder?.site?.id;
        if (!siteId) {
          toast.error('No CSpire site linked to this work order');
          return;
        }
        await cspireAPI.updateSiteTechNotes(siteId, payload);
      } else {
        const clientId = serviceCall?.client?.id;
        if (!clientId) {
          toast.error('No client linked to this assignment');
          return;
        }
        await clientsAPI.updateTechNotes(clientId, payload);
      }
      setInitialTechNotes(trimmed);
      toast.success('Tech notes saved');
      queryClient.invalidateQueries('weeklyAssignments');
      queryClient.invalidateQueries('priorityAssignments');
      queryClient.invalidateQueries('clients');
      queryClient.invalidateQueries('allClients');
      queryClient.invalidateQueries('cspireWorkOrders');
      queryClient.invalidateQueries('franklinSites');
    } catch (error) {
      console.error('Failed to save tech notes:', error);
      toast.error(error.response?.data?.message || 'Failed to save tech notes');
    } finally {
      setIsSavingTechNotes(false);
    }
  };

  const handleSubmit = async (e, action) => {
    e.preventDefault();

    const submitAction = action || actionType;
    if (!submitAction) {
      toast.error('Please select an action: Complete Service or Mark as Return');
      return;
    }

    if (!validateForm(submitAction)) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    const client = serviceCall.client;

    // Update client generator info if provided and not unreadable
    if (!formData.generatorInfoUnreadable && formData.generatorModel && formData.generatorSerialNumber) {
      // Only update if the values have changed
      if (client.generatorModel !== formData.generatorModel || 
          client.generatorSerialNumber !== formData.generatorSerialNumber) {
        try {
          await updateClientMutation.mutateAsync({
            generatorModel: formData.generatorModel.trim(),
            generatorSerialNumber: formData.generatorSerialNumber.trim()
          });
        } catch (error) {
          // Continue even if customer update fails
          console.error('Failed to update customer generator info:', error);
        }
      }
    }

    // Prepare visit data
    const visitData = {
      workPerformed: formData.workPerformed.trim(),
      outcome: submitAction === 'visit' || submitAction === 'complete' ? 'completed' : 'needs_return',
      reasonForReturn: submitAction === 'return' ? formData.returnReason.trim() : null
    };

    // For visits, set type to "visit"
    if (submitAction === 'visit') {
      const visitType = serviceCallTypes.find(t => t.name.toLowerCase() === 'visit');
      if (visitType) {
        visitData.typeId = visitType.id;
      }
    } else {
      // For normal completion or return, include parts and type
      if (formData.partsUsed.length > 0) {
        visitData.partsUsed = formData.partsUsed;
      }
      if (formData.typeId) {
        visitData.typeId = formData.typeId;
      }
      // Add service metrics if provided
      if (formData.laborHours) {
        visitData.laborHours = parseFloat(formData.laborHours);
      }
      if (formData.generatorHours) {
        visitData.generatorHours = parseFloat(formData.generatorHours);
      }
      if (formData.mileage) {
        visitData.mileage = parseFloat(formData.mileage);
      }
    }

    // Fuel level is a CSpire-only per-visit reading
    if (serviceCall?.isCspire) {
      const trimmedFuelLevel = (formData.fuelLevel || '').trim();
      visitData.fuelLevel = trimmedFuelLevel === '' ? null : trimmedFuelLevel;
    }

    try {
      // Create the visit record first
      await serviceCallVisitsAPI.createVisit(serviceCall.id, visitData);

      // Then update the service call status and typeId
      const updateData = {
        status: submitAction === 'visit' || submitAction === 'complete' ? 'completed' : 'return'
      };

      if (submitAction === 'visit' || submitAction === 'complete') {
        updateData.completedDate = new Date().toISOString();
      }

      // Include typeId in the service call update
      if (visitData.typeId) {
        updateData.typeId = visitData.typeId;
      }

      // Update the service call
      await updateServiceCallMutation.mutateAsync(updateData);
    } catch (error) {
      console.error('Error creating visit or updating service call:', error);
      toast.error(error.response?.data?.message || 'Failed to save visit information');
    }
  };

  if (!isOpen) return null;

  const client = serviceCall?.client;
  const hasExistingGeneratorInfo = client?.generatorModel && client?.generatorSerialNumber;

  return (
    <div className="fixed inset-0 z-50 bg-white">
      <div className="fixed inset-0 flex h-full w-full flex-col bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-xl font-semibold text-gray-900">
            {activeTab === 'service'
              ? 'Record Service Call'
              : activeTab === 'techNotes'
                ? 'Tech Notes'
                : 'Edit Customer Information'}
          </h3>
          <button
            onClick={onClose}
            className="icon-btn"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 px-6">
          <nav className="flex -mb-px gap-2">
            <button
              type="button"
              onClick={() => setActiveTab('service')}
              className={`py-3 px-5 text-base font-semibold border-b-2 transition-colors ${
                activeTab === 'service'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="inline h-4 w-4 mr-1" />
              Record Service
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('client')}
              className={`py-3 px-5 text-base font-semibold border-b-2 transition-colors ${
                activeTab === 'client'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Wrench className="inline h-4 w-4 mr-1" />
              Edit Customer
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('techNotes')}
              className={`py-3 px-5 text-base font-semibold border-b-2 transition-colors ${
                activeTab === 'techNotes'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <StickyNote className="inline h-4 w-4 mr-1" />
              Tech Notes
            </button>
          </nav>
        </div>

        <div className="flex-1 min-h-0">
          {activeTab === 'service' ? (
            <form onSubmit={handleSubmit} className="flex h-full min-h-0 flex-col">
              <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y px-6 py-6 space-y-5">
                {/* Client Info (Read-only) */}
                <div className="bg-gray-50 p-4 rounded-xl">
            <p className="text-sm font-medium text-gray-700">
              <span className="font-semibold">Customer:</span> {getClientDisplayName(client)}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              <span className="font-semibold">Address:</span> {client?.address}, {client?.city}, {client?.state} {client?.zipCode}
            </p>
            {hasExistingGeneratorInfo && (
              <p className="text-sm text-gray-600 mt-1">
                <span className="font-semibold">Current Generator:</span> {client.generatorModel} ({client.generatorSerialNumber})
              </p>
            )}
          </div>

          {/* Previous Visits */}
          {serviceCall?.visits && serviceCall.visits.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <History className="h-4 w-4 text-yellow-700" />
                  <h4 className="text-sm font-semibold text-yellow-900">
                    Previous Visits ({serviceCall.visits.length})
                  </h4>
                </div>
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {serviceCall.visits.map((visit) => (
                  <div key={visit.id} className="bg-white rounded p-2 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900">Visit #{visit.visitNumber}</span>
                      <span className="text-gray-500">{formatDate(visit.visitDate)}</span>
                    </div>
                    {visit.technician && (
                      <p className="text-gray-600 mb-1">
                        Tech: {visit.technician.firstName} {visit.technician.lastName}
                      </p>
                    )}
                    {visit.workPerformed && (
                      <p className="text-gray-700">{visit.workPerformed}</p>
                    )}
                    {visit.outcome === 'needs_return' && visit.reasonForReturn && (
                      <p className="text-yellow-700 mt-1 italic">
                        Return Reason: {visit.reasonForReturn}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Service Type - Hidden for visits */}
          {actionType !== 'visit' && (
            <div>
              <label className="label">
                Service Type
              </label>
              <select
                value={formData.typeId}
                onChange={(e) => setFormData({ ...formData, typeId: e.target.value })}
                className="select"
              >
                <option value="">Select service type...</option>
                {serviceCallTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Change if needed. Default is based on whether it's an oil change month.
              </p>
            </div>
          )}

          {/* Work Performed */}
          <div>
            <label className="label">
              Work Performed <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.workPerformed}
              onChange={(e) => {
                setFormData({ ...formData, workPerformed: e.target.value });
                if (errors.workPerformed) setErrors({ ...errors, workPerformed: null });
              }}
              rows={4}
              className={`input text-lg ${
                errors.workPerformed ? 'input-error' : ''
              }`}
              placeholder="Describe the work performed..."
              required
            />
            {errors.workPerformed && (
              <p className="mt-1 text-sm text-red-600">{errors.workPerformed}</p>
            )}
          </div>

          {/* Service Metrics - Hidden for visits */}
          {actionType !== 'visit' && (
            <div className={`grid grid-cols-1 ${serviceCall?.isCspire ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'} gap-4`}>
              <div>
                <label className="label">
                  Generator Hours
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.generatorHours}
                  onChange={(e) => setFormData({ ...formData, generatorHours: e.target.value })}
                  className="input"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="label">
                  Mileage
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.mileage}
                  onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                  className="input"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="label">
                  Labor Hours
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.laborHours}
                  onChange={(e) => setFormData({ ...formData, laborHours: e.target.value })}
                  className="input"
                  placeholder="0.00"
                />
              </div>
              {serviceCall?.isCspire && (
                <div>
                  <label className="label">
                    Fuel Level
                  </label>
                  <input
                    type="text"
                    maxLength={100}
                    value={formData.fuelLevel}
                    onChange={(e) => setFormData({ ...formData, fuelLevel: e.target.value })}
                    className="input"
                    placeholder='e.g. "3/4 tank" or "500 gal"'
                  />
                </div>
              )}
            </div>
          )}

          {/* Parts Used - Hidden for visits */}
          {actionType !== 'visit' && (
            <div>
              <label className="label">
                Parts Used
              </label>
            {formData.partsUsed.length > 0 && (
              <div className="mb-3 space-y-3">
                {formData.partsUsed.map((part, index) => {
                  const itemNumber = part.itemNumber || part.name || 'N/A';
                  const description = part.description || part.name || '';
                  const quantity = parseFloat(part.quantity) || 0;
                  const cost = parseFloat(part.cost) || 0;
                  return (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-xl gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-base font-medium text-gray-900">{itemNumber}</div>
                        {description && <div className="text-sm text-gray-600">{description}</div>}
                        <div className="text-sm text-gray-500 mt-1">
                          Qty: {quantity.toFixed(2)} @ {formatCurrency(cost)} = {formatCurrency(quantity * cost)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemovePart(index)}
                        className="icon-btn-danger flex-shrink-0"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="space-y-4">
              {/* Toggle between inventory search and custom part */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">
                  {useCustomPart ? 'Custom Part Mode' : 'Inventory Search Mode'}
                </span>
                <button
                  type="button"
                  onClick={handleToggleMode}
                  className="link-btn"
                >
                  {useCustomPart ? 'Search Inventory Instead' : 'Add Custom Part Instead'}
                </button>
              </div>

              {useCustomPart ? (
                // Custom part entry mode
                <div className="space-y-4">
                  <div>
                    <label className="label">Part Name/Number *</label>
                    <input
                      type="text"
                      placeholder="Enter part name or number"
                      value={newPart.name}
                      onChange={(e) => setNewPart({ ...newPart, name: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="label">Description (optional)</label>
                    <input
                      type="text"
                      placeholder="Enter part description"
                      value={newPart.description}
                      onChange={(e) => setNewPart({ ...newPart, description: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="label">Quantity *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        placeholder="1.00"
                        value={newPart.quantity}
                        onChange={(e) => setNewPart({ ...newPart, quantity: e.target.value })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="label">Cost *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={newPart.cost}
                        onChange={(e) => setNewPart({ ...newPart, cost: e.target.value })}
                        className="input"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleAddPart}
                        disabled={!newPart.name || !newPart.quantity || !newPart.cost}
                        className="btn btn-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="h-5 w-5 mr-1" />
                        Add Part
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // Inventory search mode
                <>
                  <InventorySearch 
                    onSelect={handleInventorySelect}
                    placeholder="Search for parts by item number or description..."
                    resetKey={searchResetKey}
                  />
                  {selectedInventoryItem && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-base">
                      <div className="font-medium text-blue-900">{selectedInventoryItem.itemNumber}</div>
                      <div className="text-blue-700">{selectedInventoryItem.description}</div>
                      <div className="text-blue-600 mt-1">Price: ${parseFloat(selectedInventoryItem.price || selectedInventoryItem.cost).toFixed(2)}</div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="label">Quantity</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        placeholder="1.00"
                        value={newPart.quantity}
                        onChange={(e) => setNewPart({ ...newPart, quantity: e.target.value })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="label">Price (override)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={selectedInventoryItem ? `$${parseFloat(selectedInventoryItem.price || selectedInventoryItem.cost).toFixed(2)}` : "Price"}
                        value={newPart.cost}
                        onChange={(e) => setNewPart({ ...newPart, cost: e.target.value })}
                        className="input"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleAddPart}
                        disabled={!selectedInventoryItem || !newPart.quantity}
                        className="btn btn-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="h-5 w-5 mr-1" />
                        Add Part
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          )}

          {/* Generator Information - Hidden for visits */}
          {actionType !== 'visit' && (
          <div className="border-t pt-5">
            <div className="flex items-center justify-between mb-3">
              <label className="label mb-0">
                Generator Information {!hasExistingGeneratorInfo && <span className="text-red-500">*</span>}
              </label>
              {!hasExistingGeneratorInfo && (
                <label htmlFor="generatorInfoUnreadable" className="flex items-center gap-3 cursor-pointer py-2 px-3 rounded-xl active:bg-gray-100">
                  <input
                    type="checkbox"
                    id="generatorInfoUnreadable"
                    checked={formData.generatorInfoUnreadable}
                    onChange={(e) => {
                      setFormData({ ...formData, generatorInfoUnreadable: e.target.checked });
                      setErrors({ ...errors, generatorModel: null, generatorSerialNumber: null });
                    }}
                    className="checkbox"
                  />
                  <span className="text-base text-gray-600">Unreadable/Missing</span>
                </label>
              )}
            </div>
            {!formData.generatorInfoUnreadable && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">
                    Model {!hasExistingGeneratorInfo && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="text"
                    value={formData.generatorModel}
                    onChange={(e) => {
                      setFormData({ ...formData, generatorModel: e.target.value });
                      if (errors.generatorModel) setErrors({ ...errors, generatorModel: null });
                    }}
                    className={`input ${
                      errors.generatorModel ? 'input-error' : ''
                    }`}
                    placeholder="e.g., Generac 22kW"
                    disabled={formData.generatorInfoUnreadable}
                  />
                  {errors.generatorModel && (
                    <p className="mt-1 text-sm text-red-600">{errors.generatorModel}</p>
                  )}
                </div>
                <div>
                  <label className="label">
                    Serial Number {!hasExistingGeneratorInfo && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="text"
                    value={formData.generatorSerialNumber}
                    onChange={(e) => {
                      setFormData({ ...formData, generatorSerialNumber: e.target.value });
                      if (errors.generatorSerialNumber) setErrors({ ...errors, generatorSerialNumber: null });
                    }}
                    className={`input ${
                      errors.generatorSerialNumber ? 'input-error' : ''
                    }`}
                    placeholder="e.g., 12345678"
                    disabled={formData.generatorInfoUnreadable}
                  />
                  {errors.generatorSerialNumber && (
                    <p className="mt-1 text-sm text-red-600">{errors.generatorSerialNumber}</p>
                  )}
                </div>
              </div>
            )}
            {formData.generatorInfoUnreadable && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5 mr-2" />
                  <p className="text-sm text-yellow-800">
                    Generator information marked as unreadable. Please ensure you've checked thoroughly before proceeding.
                  </p>
                </div>
              </div>
            )}
            {!hasExistingGeneratorInfo && !formData.generatorInfoUnreadable && (
              <p className="mt-2 text-xs text-gray-500">
                <span className="font-medium">Important:</span> Please add generator information if available. This helps with future service calls.
              </p>
            )}
          </div>
          )}

          {/* Return Reason (only shown when marking as return) */}
          {actionType === 'return' && (
            <div>
              <label className="label">
                Return Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.returnReason}
                onChange={(e) => {
                  setFormData({ ...formData, returnReason: e.target.value });
                  if (errors.returnReason) setErrors({ ...errors, returnReason: null });
                }}
                rows={3}
                className={`input ${
                  errors.returnReason ? 'input-error' : ''
                }`}
                placeholder="Explain why this service call needs to be returned (e.g., need to order part, generator inaccessible, etc.)"
                required
              />
              {errors.returnReason && (
                <p className="mt-1 text-sm text-red-600">{errors.returnReason}</p>
              )}
            </div>
          )}

              </div>
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-between gap-3 border-t border-slate-200 bg-white px-6 py-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary w-full sm:w-auto"
              >
                Cancel
              </button>
              {localStorage.getItem(`serviceCallDraft_${serviceCall?.id}`) && (
                <button
                  type="button"
                  onClick={handleClearDraft}
                  className="btn w-full sm:w-auto border border-red-300 text-red-700 bg-white hover:bg-red-50"
                  title="Clear saved draft and start fresh"
                >
                  <X className="h-4 w-4 inline mr-1.5" />
                  Clear Draft
                </button>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setActionType('visit');
                  handleSubmit(e, 'visit');
                }}
                disabled={updateServiceCallMutation.isLoading}
                className={`btn w-full sm:w-auto border ${
                  actionType === 'visit'
                    ? 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700'
                    : 'bg-white text-purple-700 border-purple-300 hover:bg-purple-50'
                } disabled:opacity-50`}
              >
                <FileText className="h-4 w-4 inline mr-1.5" />
                Mark as Visit
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setActionType('return');
                  handleSubmit(e, 'return');
                }}
                disabled={updateServiceCallMutation.isLoading}
                className={`btn w-full sm:w-auto border ${
                  actionType === 'return'
                    ? 'bg-yellow-600 text-white border-yellow-600 hover:bg-yellow-700'
                    : 'bg-white text-yellow-700 border-yellow-300 hover:bg-yellow-50'
                } disabled:opacity-50`}
              >
                <RotateCcw className="h-4 w-4 inline mr-1.5" />
                Mark as Return
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setActionType('complete');
                  handleSubmit(e, 'complete');
                }}
                disabled={updateServiceCallMutation.isLoading}
                className="btn w-full sm:w-auto bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              >
                <Check className="h-4 w-4 inline mr-1.5" />
                {updateServiceCallMutation.isLoading ? 'Saving...' : 'Complete Service'}
              </button>
            </div>
          </div>
            </form>
        ) : activeTab === 'client' ? (
          <form onSubmit={handleClientSubmit} className="flex h-full min-h-0 flex-col">
            <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y px-6 py-6 space-y-5">
              {/* Client Editing Form */}
              <div className="space-y-4">
              {/* Generator Information */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                  <Wrench className="h-5 w-5 mr-2 text-blue-600" />
                  Generator Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Type</label>
                    <input
                      type="text"
                      name="generatorType"
                      value={clientFormData.generatorType}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="e.g., Standby"
                    />
                  </div>
                  <div>
                    <label className="label">Model</label>
                    <input
                      type="text"
                      name="generatorModel"
                      value={clientFormData.generatorModel}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="Model number"
                    />
                  </div>
                  <div>
                    <label className="label">Serial Number</label>
                    <input
                      type="text"
                      name="generatorSerialNumber"
                      value={clientFormData.generatorSerialNumber}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="Serial number"
                    />
                  </div>
                  <div>
                    <label className="label">Size</label>
                    <input
                      type="text"
                      name="generatorSize"
                      value={clientFormData.generatorSize}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="e.g., 50kW"
                    />
                  </div>
                  <div>
                    <label className="label">Cooling Type</label>
                    <select
                      name="generatorCoolingType"
                      value={clientFormData.generatorCoolingType}
                      onChange={handleClientChange}
                      className="input"
                    >
                      <option value="">Unknown</option>
                      <option value="air">Air-cooled</option>
                      <option value="water">Water-cooled</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="label">Exercise Time</label>
                    <input
                      type="text"
                      name="generatorExerciseTime"
                      value={clientFormData.generatorExerciseTime}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="e.g., 2:00 PM"
                    />
                  </div>
                </div>
              </div>

              {/* Filters & Fluids */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                  <Wrench className="h-5 w-5 mr-2 text-amber-600" />
                  Filters & Fluids
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Air Filter</label>
                    <input
                      type="text"
                      name="airFilter"
                      value={clientFormData.airFilter}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="Part number"
                    />
                  </div>
                  <div>
                    <label className="label">Fuel Filter 1</label>
                    <input
                      type="text"
                      name="fuelFilter1"
                      value={clientFormData.fuelFilter1}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="Part number"
                    />
                  </div>
                  <div>
                    <label className="label">Fuel Filter 2</label>
                    <input
                      type="text"
                      name="fuelFilter2"
                      value={clientFormData.fuelFilter2}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="Part number"
                    />
                  </div>
                  <div>
                    <label className="label">Oil Filter 1</label>
                    <input
                      type="text"
                      name="oilFilter1"
                      value={clientFormData.oilFilter1}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="Part number"
                    />
                  </div>
                  <div>
                    <label className="label">Oil Filter 2</label>
                    <input
                      type="text"
                      name="oilFilter2"
                      value={clientFormData.oilFilter2}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="Part number"
                    />
                  </div>
                  <div>
                    <label className="label">Fuel Additive</label>
                    <input
                      type="text"
                      name="fuelAdditive"
                      value={clientFormData.fuelAdditive}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="Type or brand"
                    />
                  </div>
                  <div>
                    <label className="label">Fuel Type</label>
                    <input
                      type="text"
                      name="fuelType"
                      value={clientFormData.fuelType}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="e.g., Diesel"
                    />
                  </div>
                  <div>
                    <label className="label">Oil Type</label>
                    <input
                      type="text"
                      name="oilType"
                      value={clientFormData.oilType}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="e.g., 15W-40"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="label">Oil Amount</label>
                    <input
                      type="text"
                      name="oilAmount"
                      value={clientFormData.oilAmount}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="e.g., 5 quarts"
                    />
                  </div>
                </div>
              </div>

              {/* Battery Information */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                  <Battery className="h-5 w-5 mr-2 text-green-600" />
                  Battery Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="label">Battery Size</label>
                    <input
                      type="text"
                      name="batterySize"
                      value={clientFormData.batterySize}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="e.g., Group 24"
                    />
                  </div>
                  <div>
                    <label className="label">Date Code</label>
                    <input
                      type="text"
                      name="batteryDateCode"
                      value={clientFormData.batteryDateCode}
                      onChange={handleClientChange}
                      maxLength={5}
                      className="input"
                      placeholder="MM/YY"
                    />
                  </div>
                  <div>
                    <label className="label">Date Changed</label>
                    <input
                      type="date"
                      name="batteryDateChanged"
                      value={clientFormData.batteryDateChanged}
                      onChange={handleClientChange}
                      className="input"
                    />
                  </div>
                </div>
              </div>
            </div>

            </div>
            <div className="flex flex-wrap justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateClientMutation.isLoading}
                className="btn btn-primary disabled:opacity-50"
              >
                {updateClientMutation.isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
            </form>
          ) : (
            <form onSubmit={handleSaveTechNotes} className="flex h-full min-h-0 flex-col">
              <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y px-6 py-6 space-y-5">
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5">
                  <div className="flex items-start gap-3">
                    <StickyNote className="h-5 w-5 mt-0.5 text-yellow-700 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-yellow-900">Tech Notes</h4>
                      <p className="text-sm text-yellow-800 mt-1">
                        Short note that every tech sees on the assignment card every time
                        they service this {serviceCall?.isFranklin ? 'Franklin site' : serviceCall?.isCspire ? 'CSpire site' : 'client'}.
                        Use this for things like gate quirks, dog warnings, parking notes, equipment access tips, etc.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="techNotesField" className="label">Note (visible to all techs)</label>
                  <textarea
                    id="techNotesField"
                    value={techNotes}
                    onChange={(e) => setTechNotes(e.target.value)}
                    maxLength={1000}
                    rows={6}
                    className="input"
                    placeholder="e.g., Gate is on the left side of the driveway, code is 1234. Watch out for the dog around the back of the building."
                  />
                  <p className="mt-1 text-xs text-gray-500 text-right">
                    {techNotes.length}/1000
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
                <button
                  type="button"
                  onClick={() => setTechNotes(initialTechNotes)}
                  disabled={isSavingTechNotes || techNotes === initialTechNotes}
                  className="btn btn-secondary disabled:opacity-50"
                >
                  Reset
                </button>
                <button
                  type="submit"
                  disabled={isSavingTechNotes || (techNotes || '').trim() === (initialTechNotes || '').trim()}
                  className="btn btn-primary disabled:opacity-50"
                >
                  {isSavingTechNotes ? 'Saving...' : 'Save Tech Notes'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
